import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  HStack,
  type MenuListProps,
  type MenuProps,
  PopoverAnchor,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { FilterPopover } from "@src/utils/components/filters/components/filter-popover";
import { FilterTemplateSelect } from "@src/utils/components/filters/components/filter-template-select";
import { useFiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { MoreFiltersButton } from "@src/utils/components/filters/components/more-filters-button";
import type { TableFilter } from "@src/utils/components/filters/factories";
import { observer } from "mobx-react-lite";
import type { FunctionComponent } from "react";

type TFiltersPanelFiltersProps = {
  menuPlacement?: MenuProps["placement"];
  innerMenuPlacement?: MenuProps["placement"];
  menuListZIndex?: MenuListProps["zIndex"];
  clearButtonSide?: "left" | "right";
};

export const FiltersPanelFilters: FunctionComponent<TFiltersPanelFiltersProps> =
  observer(function FiltersPanelFilters({ clearButtonSide = "left" }) {
    const { filters, filterComponents, appliedCount } = useFiltersPanel();
    const { isOpen, onOpen, onClose } = useDisclosure();

    return (
      <>
        <HStack
          spacing="2"
          flexDir={clearButtonSide === "left" ? "row" : "row-reverse"}
        >
          {appliedCount > 0 && (
            <Button
              onClick={() => filters.clearAllVisibleFilters()}
              size="sm"
              variant="link"
            >
              <Trans>Clear filters</Trans>
            </Button>
          )}

          <MoreFiltersButton
            appliedCount={appliedCount}
            onClick={onOpen}
            templateName={
              filters.isCurrentFiltersMatchingSelectedTemplate
                ? filters.selectedTemplate?.name
                : undefined
            }
          />
        </HStack>

        <Drawer
          blockScrollOnMount={false}
          isOpen={isOpen}
          onClose={onClose}
          placement="right"
          trapFocus={false}
        >
          <DrawerOverlay />

          <DrawerContent maxW="400px">
            <HStack justify="space-between" px="6" py="4">
              <Text color="grey.800" fontSize="md" fontWeight="bold">
                <Trans>Filters</Trans>
              </Text>

              <Button
                onClick={() => filters.clearAllVisibleFilters()}
                size="sm"
                variant="link"
              >
                <Trans>Clear all</Trans>
              </Button>
            </HStack>

            <DrawerBody px="0" py="0">
              <FilterTemplateSelect />

              <Box px="6">
                {filterComponents.map((filter) => (
                  <FilterRow key={filter.key} tableFilter={filter} />
                ))}
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  });

const FilterRow: FunctionComponent<{ tableFilter: TableFilter }> = observer(
  function FilterRow({ tableFilter }) {
    const {
      key,
      label,
      selectedCount,
      selectedLabel,
      selectedTooltip,
      belowContent,
    } = tableFilter;
    const isSelected = selectedCount > 0;

    return (
      <Box
        borderBottomWidth="1px"
        borderColor="grey.100"
        _last={{ borderBottomWidth: 0 }}
      >
        <FilterPopover
          targetId={key}
          filter={tableFilter}
          placement="bottom-end"
          trigger={
            <HStack
              as="button"
              type="button"
              align="center"
              justify="space-between"
              w="full"
              p="0"
              py="3"
              fontWeight="normal"
              spacing="4"
            >
              <Text color="grey.800" flexShrink={0} fontSize="sm">
                {label}
              </Text>

              <PopoverAnchor>
                <Box display="inline-flex" alignItems="center" gap="1" minW="0">
                  <Tooltip
                    hasArrow
                    isDisabled={!selectedTooltip}
                    label={selectedTooltip}
                    placement="top"
                  >
                    <Text
                      overflow="hidden"
                      color={isSelected ? "grey.800" : "grey.400"}
                      fontSize="sm"
                      fontWeight={isSelected ? "bold" : "normal"}
                      textAlign="right"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                    >
                      {selectedLabel}
                    </Text>
                  </Tooltip>

                  <Icon
                    name="chevron-down"
                    w="4"
                    h="4"
                    color="grey.400"
                    transition="transform 0.15s"
                    sx={{
                      '[aria-expanded="true"] &': {
                        transform: "rotate(180deg)",
                      },
                    }}
                  />
                </Box>
              </PopoverAnchor>
            </HStack>
          }
        />

        {belowContent}
      </Box>
    );
  },
);
